"use client";
import { useEffect, useState, Fragment, useCallback } from "react";
import { useScopedI18n } from "@/messages/client";
import Title from "@/components/ui/title";
import { DirectusImage } from "@/components/DirectusImage";
import RouterLink from "@/components/ui/router-link";
import { Button } from "@/components/ui/button";
import { ChevronsRight } from "lucide-react";
import "./index.css";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

const tabs = ["应用方案", "客户案例"];

function MobileCarousel(props: {
  data?: {
    key: string;
    banner?: string;
    title?: string;
    href?: string;
  }[];
}) {
  const tCommon = useScopedI18n("通用");
  const [current, setCurrent] = useState(0);
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }
    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    setScrollSnaps(mainApi.scrollSnapList());

    mainApi.on("reInit", () => {
      setScrollSnaps(mainApi.scrollSnapList());
    });
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  if (!props.data) {
    return null;
  }
  return (
    <Carousel className="mt-7 w-full" opts={{ loop: true }} setApi={setMainApi}>
      <CarouselContent className="-ml-2">
        {props.data.map((el) => (
          <CarouselItem key={el.key} className="pl-2">
            <div className="w-full select-none">
              <div className="flex h-[152px] w-full items-center justify-center overflow-hidden rounded-t-xl">
                <DirectusImage
                  id={el.banner || ""}
                  alt={el.title || ""}
                  width={1200}
                  height={560}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex h-[100px] w-full flex-col justify-between rounded-b-xl bg-zinc-100 px-4 py-3">
                <p className="line-clamp-2 text-ellipsis whitespace-pre-line text-base">
                  {el.title}
                </p>
                {el.href && (
                  <RouterLink
                    href={el.href}
                    className="self-end text-xs text-gray">
                    {tCommon("了解更多")}
                    <ChevronsRight className="inline" size={12} />
                  </RouterLink>
                )}
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="mt-4 flex w-full items-center justify-center space-x-2 md:hidden">
        {scrollSnaps.map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-1.5 w-1.5 rounded-full bg-[#CCCCCC] transition-all duration-300 ease-in-out",
              {
                "w-4 bg-[#2C2C2C]": current === index,
              }
            )}></div>
        ))}
      </div>
    </Carousel>
  );
}

export default function SolutionAndCaseClient(props: {
  solutions?: {
    slug?: string;
    title?: string;
    image?: string;
    button_link?: string;
    button_text?: string;
    radio: "related" | "custom";
  }[];
  cases?: {
    slug?: string;
    title?: string;
    image?: string;
    button_link?: string;
    button_text?: string;
    radio: "related" | "custom";
  }[];
}) {
  const t = useScopedI18n("应用");
  const tCommon = useScopedI18n("通用");

  const [tab, setTab] = useState(tabs[0]);
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);

  const getBannerData = useCallback(
    (tab: string) => {
      if (tab === tabs[0]) {
        return props.solutions?.map((el, index) => ({
          key: `${tabs[0]}_${index}`,
          href: el.button_link,
          title: `${el.title}
          ${t("解决方案")}`,
          banner: el.image,
          button_text: el.button_text,
        }));
      }
      if (tab === tabs[1]) {
        return props.cases?.map((el, index) => ({
          key: `${tabs[1]}_${index}`,
          href: el.button_link,
          title: el.title,
          banner: el.image,
          button_text: el.button_text,
        }));
      }
      return undefined;
    },
    [props, t]
  );

  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }

    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  useEffect(() => {
    setCurrent(0);
  }, [tab]);

  return (
    <div className="w-full">
      <Title title={t("方案与案例")} />

      <div className="hidden md:block">
        {/* <div className="mt-10 flex items-center justify-center gap-x-12">
          {tabs.map((el, index) => (
            <Fragment key={index}>
              {index !== 0 && <hr className="h-8 w-0 border-l"></hr>}
              <span
                onClick={() => setTab(el)}
                className={cn(
                  "select-none text-xl",
                  tab !== el && "cursor-pointer opacity-80 hover:text-primary",
                  tab === el && "cursor-default font-bold text-primary"
                )}>
                {t(el as any)}
              </span>
            </Fragment>
          ))}
        </div> */}
        <div className="relative w-full">
          <Carousel
            className="mt-7 w-full"
            opts={{ loop: true }}
            setApi={setMainApi}>
            <CarouselContent className="m-0">
              {getBannerData(tab)?.map((el, index) => (
                <CarouselItem
                  key={el.key}
                  className="basis-[calc(62.5vw+32px)] p-4">
                  <div
                    className={cn("relative h-[560px] w-full select-none", {
                      "opacity-60": current !== index,
                    })}>
                    <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center overflow-hidden rounded-2xl">
                      <DirectusImage
                        id={el.banner || ""}
                        alt={el.title || ""}
                        width={1200}
                        height={560}
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute left-[72px] top-40">
                        <p className="min-h-[112px] whitespace-pre-line text-4xl leading-normal text-white mix-blend-difference">
                          {el.title}
                        </p>
                        {el.href && (
                          <RouterLink href={el.href}>
                            <Button className="mt-12 min-w-[160px] rounded-md text-lg">
                              {el.button_text || tCommon("了解更多")}
                            </Button>
                          </RouterLink>
                        )}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="cases-prev" />
            <CarouselNext className="cases-next" />
          </Carousel>
        </div>
      </div>
      {/* <div className="md:hidden">
        {tabs.map((tab) => (
          <div key={tab} className="w-full px-6">
            <p className="mt-4 text-center text-sm">{t(tab as any)}</p>
            <MobileCarousel key={tab} data={getBannerData(tab)} />
          </div>
        ))}
      </div> */}
    </div>
  );
}
